import type { ThemeMode } from './roleTokenValues';

/**
 * The mode `role` (in `roleTokens.ts`) and `themedStyleSheet` resolve against
 * on native. Web needs none of this: `roleTokens.web.ts` hands out `var()`
 * strings that the browser resolves itself, so the mode never has to be known
 * in JS. Native has no such indirection at the style layer, so something has
 * to hold "which mode is current" as plain, synchronously-readable state that
 * a `Proxy` `get` trap can consult on every property read.
 *
 * Deliberately not `roleTokenValues.ts` (the light/dark colour maps) or
 * shared-logic's `useTheme` (the persisted preference): this is neither data
 * nor a preference, it is the one piece of mutable rendering state shared-ui's
 * native Proxies read from. `apps/gripp-app/src/app/_layout.tsx` is the only
 * writer, syncing it from `useTheme().resolvedTheme` once, high in the tree.
 * No `.web.ts` sibling: the code here is plain JS with no RN or DOM API, so
 * there is nothing platform-specific to shadow — it is simply never imported
 * from a `.web.ts` file, only from native's `roleTokens.ts`, `useThemeMode.ts`,
 * and `themedStyleSheet.ts`.
 */
let currentMode: ThemeMode = 'light';
const listeners = new Set<() => void>();

export const getThemeMode = (): ThemeMode => currentMode;

/**
 * A `useSyncExternalStore` subscriber list, not a React re-render trigger by
 * itself: setting the mode notifies listeners, but a `Proxy` `get` trap does
 * not schedule a render. `_layout.tsx` calling `useThemeMode()` once, high in
 * the tree, is what turns a notification into the app-wide repaint — see that
 * file for why a `key` remount is deliberately not used instead.
 */
export const setThemeMode = (mode: ThemeMode): void => {
  if (mode === currentMode) return;
  currentMode = mode;
  listeners.forEach((notify) => notify());
};

export const subscribeToThemeMode = (onChange: () => void): (() => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};
